import React from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardStack, SectionGrid } from './dashboard/styles';
import StatsCards from './dashboard/cards/StatsCards';
import ProfileCard from './dashboard/cards/ProfileCard';
import LeaveSummaryCard from './dashboard/cards/LeaveSummaryCard';
import QuickActionsCard from './dashboard/cards/QuickActionsCard';
import OnLeaveTodayCard from './dashboard/cards/OnLeaveTodayCard';
import BirthdayCard from './dashboard/cards/BirthdayCard';
import AnniversaryCard from './dashboard/cards/AnniversaryCard';
import PersonnelCard from './dashboard/cards/PersonnelCard';
import OpenEnrollmentProgressCard from './dashboard/cards/OpenEnrollmentProgressCard';
import AnnouncementsCard from './dashboard/cards/AnnouncementsCard';
import RecognitionWallCard from './dashboard/cards/RecognitionWallCard';
import ContinueLearningCard from './dashboard/cards/ContinueLearningCard';
import MyCertificatesCard from './dashboard/cards/MyCertificatesCard';
import TeamVacancyCard from './dashboard/cards/TeamVacancyCard';
import DepartmentDistributionCard from './dashboard/cards/DepartmentDistributionCard';
import AgeDistributionCard from './dashboard/cards/AgeDistributionCard';
import SeniorityDistributionCard from './dashboard/cards/SeniorityDistributionCard';
import HaIntelligencePanel from './dashboard/cards/HaIntelligencePanel';
import { resolveHasHaModule } from './dashboard/resolveHasHaModule';
import { resolveCanUsePm } from './dashboard/resolveCanUsePm';
import { resolveDashboardGates } from './dashboard/resolveDashboardGates';

// ═══════════ YERLEŞİM: SABİT SPAN YERİNE KENDİ KENDİNİ DENGELEYEN BÖLÜMLER ═══════════
// Panel eskiden 12 sütunluk TEK bir ızgaraydı ve her kart sabit bir `span` taşıyordu. Kartların
// çoğu MODÜLE BAĞLI koşullu render edildiği için yerleşim müşteriden müşteriye bozuluyordu:
// İzin ve Anket modülü olmayan bir müşteride üstteki dört kutudan ikisi düşüyor ve satırın
// yarısı boş kalıyordu; boşluğa sonraki bölümün kartı yukarı çekiliyordu. (Grafik satırı ise
// 8+3=11 sütunla, hiçbir modül eksik olmasa bile zaten eksikti.)
//
// Artık her bölüm kendi esnek satırına sahip: kart genişliği elle atanmaz, satırın boş alanı
// O SATIRDAKİ kartlara dağıtılır. Hangi modüllerin açık olduğundan bağımsız olarak satırlar tam
// dolar — SON satır da dâhil (grid `auto-fit` yalnız hiç kart almayan kolonları toplar, kısmen
// dolu son satırı düzeltmezdi; delik oradan geliyordu). Bölümler birbirinin içine akamaz.
//
// Genişlik farkı gereken yerde kart PAYINI bildirir ($grow), piksel/sütun değil: departman
// grafiği 9, Personelim 3 — oran her ekran genişliğinde korunur.
//
// `$min` değerleri bölümün İÇERİĞİNE göre: istatistik kutuları dar (240px), içerik kartları
// orta (340px), grafikler geniş (380px) sığar.
export default function DashboardPage() {
  const { canRead, canWrite, hasModule, hasSeat, hasPermission } = useAuth();

  // Kişisel kart (abonelik + koltuk) ile şirket geneli kart (ayrıcalık) AYRI kapılardır —
  // eskiden ikisi de canRead'di ve rolsüz çalışan kendi izin bakiyesini göremiyordu.
  const { canSeeOnLeaveToday, canUseLeaves, canUseSurveys } =
    resolveDashboardGates({ canRead, hasModule, hasSeat });
  // Benefit admin-only card: standard users only see "Yan Haklarım" — the
  // open-enrollment widget is management/HR surface.
  const hasBenefitAdmin = canWrite('BN');
  const hasAnnouncementModule = canRead('AN');
  const hasTrainingModule = canRead('TR');
  // Faz D (spec 2026-08-08 §4, Task 5) — takdir duvarı kartı: SidebarMenu'nün
  // performans gelen kutusu rozetiyle AYNI ilkel — self-servis yüzey, kullanıcının
  // kendi PM.Read ayrıcalığı ARANMAZ (recognitions uçları [Authorize]-only + PM
  // aboneliği serviste, task-3-report.md §1). Ruling 64: ilkel artık abonelik VE
  // koltuk (resolveCanUsePm) — ölçümlü PM'de koltuksuz kişinin her pano ziyaretinde
  // 403 + refresh-session + hata toast'ı üretmemesi için; ölçümsüzde bugünkü gibi.
  const hasPmModule = resolveCanUsePm({ hasModule, hasSeat });
  // canRead('HA') TEK BAŞINA yeterli değil — enterpriseAdmin için hasPermission koşulsuz true
  // döner, tenant'ın HA aboneliğine bakmadan. Ayrıntılı gerekçe: resolveHasHaModule.js.
  const hasHaModule = resolveHasHaModule({ hasModule, canRead });

  return (
    <DashboardStack>
      {/* Özet kutuları — sayıları modüle göre 2 ile 4 arasında değişir, satır her hâlde dolar. */}
      <SectionGrid $min="240px">
        <StatsCards canSeeOnLeaveToday={canSeeOnLeaveToday} canUseSurveys={canUseSurveys} />
      </SectionGrid>

      {/* İK Zekâsı — HA modülü yoksa HİÇ mount edilmez: kilitli kart, bulanık önizleme ya da
          "yükselt" teaser'ı yok (spec §13.1, Karar 5). Panelin kendi içindeki hasHaModule
          kontrolü buradaki AYNI değeri okur; ikinci bir hesaplama değil, refactor güvencesi. */}
      {hasHaModule && <HaIntelligencePanel hasHaModule={hasHaModule} />}

      {/* Kişisel kartlar. Beş "bekleyen onay" kartı (izin, masraf, yan hak, özgeçmiş,
          eğitim) buradan kaldırıldı — onların yerini /approvals'taki tek panel aldı
          (bkz. spec 2026-08-05-onaylar-paneli, Görev 8). Bu bölüm hâlâ çoğunlukla
          modüle bağlı: yalnız Profil ve Hızlı İşlemler koşulsuz. */}
      <SectionGrid $min="340px">
        <ProfileCard />
        {/* Yönetici kartı: alt dalında boş ya da sahibi ayrılan pozisyon varsa çizilir;
            ekibi olmayan kullanıcıda kendisi null döner (kendi yüklemesi, modül kapısı yok). */}
        <TeamVacancyCard />
        {canUseLeaves && <LeaveSummaryCard />}
        <QuickActionsCard hasModule={hasModule} hasSeat={hasSeat} hasPermission={hasPermission} />

        {hasBenefitAdmin && <OpenEnrollmentProgressCard />}
        {hasTrainingModule && <ContinueLearningCard />}
        {hasTrainingModule && <MyCertificatesCard />}
        {hasAnnouncementModule && <AnnouncementsCard />}
        {hasPmModule && <RecognitionWallCard />}
      </SectionGrid>

      {/* Listeler — ikisi koşulsuz, biri izin modülüne bağlı. */}
      <SectionGrid $min="340px">
        {canSeeOnLeaveToday && <OnLeaveTodayCard />}
        <BirthdayCard />
        <AnniversaryCard />
      </SectionGrid>

      {/* Departman dağılımı çok kategorili, geniş olmalı; Personelim dar bir özet. İkisi AYNI
          satırda 9'a 3 oranıyla paylaşır — tam satır yapılsalardı ikisi de tek başına satırda
          kalıp gereksiz yere yayılıyordu. Oran piksel değil pay olduğu için her ekran
          genişliğinde korunur; dar ekranda alt sınırlarına takılıp alt alta geçerler. */}
      <SectionGrid $min="380px">
        <DepartmentDistributionCard />
        <PersonnelCard />
      </SectionGrid>

      {/* Yaş ve kıdem — eşit iki sütun. */}
      <SectionGrid $min="380px">
        <AgeDistributionCard />
        <SeniorityDistributionCard />
      </SectionGrid>
    </DashboardStack>
  );
}
